import { Bike, Check, PackageCheck } from "lucide-react";
import type { TripStatus } from "@/entities/trip";
import { colors } from "@/shared/design-system";
import { AppText } from "@/shared/ui";

// Progression en TROIS jalons, à l'horizontale, reliés par un rail continu.
//
// Le `Timeline` du design system n'est pas réutilisé : il est VERTICAL et
// attend un horodatage par étape, que le contrat ne fournit pas (les
// transitions de course ne sont pas datées). Une pile verticale mangerait en
// plus la hauteur nécessaire à la mini-carte, dans une colonne de 22rem.

const STEPS = [
  { key: "prep", label: "Préparation", Icon: Check },
  { key: "route", label: "En route", Icon: Bike },
  { key: "delivery", label: "Livraison", Icon: PackageCheck },
] as const;

/**
 * États qu'une progression LINÉAIRE représente mal.
 *
 * Correction du 2026-09-05 : « Une progression strictement linéaire les
 * représente mal : mieux vaut ne rien afficher que d'annoncer un recul comme
 * une avancée. »
 *
 * `APPROVAL_EXPIRED` mène toujours à `CANCELLED` ; les trois autres sont des
 * fins. Aucun n'est un point du parcours : les ranger sur un rail les
 * afficherait comme une étape, et le `default` ci-dessous les aurait montrés
 * tout au DÉBUT — un échec présenté comme une préparation.
 */
const OFF_LADDER = new Set<TripStatus>([
  "APPROVAL_EXPIRED",
  "CANCELLED",
  "DISPUTED",
  "REFUNDED",
  // Le document le nomme au même titre : « le second est un aller-retour — la
  // course peut revenir à `WALLET_FUNDS_BLOCKED` si la main refuse ». On s'aligne
  // sur sa lettre, malgré une réserve qui vaut d'être écrite : ces deux états
  // occupent le MÊME jalon, donc le retour ne fait reculer aucune pastille. Le
  // masquer fait en revanche disparaître puis réapparaître la progression au fil
  // de la rotation des offres. Si ce clignotement gêne à l'usage, c'est cette
  // ligne qu'il faut retirer.
  "AGENT_OFFER_PENDING",
]);

/**
 * Jalon atteint par un statut de course, ou `null` s'il n'a pas sa place ici.
 *
 * Le cycle réel est PENDING → BLOCKED → ASSIGNED → EN_ROUTE → IN_PROGRESS →
 * AWAITING_APPROVAL → DELIVERING → DELIVERED. On le replie sur trois jalons :
 * `IN_PROGRESS` (la Main fait les achats) et la validation client tombent dans
 * « En route », car du point de vue du client la Main est en mission — rien
 * n'est encore parti vers chez lui.
 *
 * ## Pourquoi `null` plutôt qu'un repli sur zéro
 * Le `default` renvoyait `0` : tout statut inconnu ou terminal se peignait en
 * « Préparation ». Le panneau appelant ne montre aujourd'hui que des courses
 * vivantes, ce qui masquait le défaut — mais c'est un couplage, pas une
 * garantie, et il suffirait d'élargir sa liste pour afficher un échec comme un
 * début. Le composant se protège donc lui-même.
 *
 */
function stepIndexFor(status: TripStatus | undefined): number | null {
  if (status && OFF_LADDER.has(status)) return null;

  switch (status) {
    case "AGENT_EN_ROUTE":
    case "IN_PROGRESS":
    case "AWAITING_CLIENT_APPROVAL":
      return 1;
    case "DELIVERING":
      return 2;
    case "DELIVERED":
    case "PAYMENT_SETTLED":
      return 3; // tout est franchi
    case "PENDING_WALLET_CHECK":
    case "WALLET_FUNDS_BLOCKED":
    case "AGENT_ASSIGNED":
      return 0;
    default:
      // Statut inconnu — un état ajouté au contrat que cette échelle ignore.
      // Ne rien afficher vaut mieux que le placer au hasard.
      return null;
  }
}

/** Segment de rail. Invisible aux extrémités, pour garder les pastilles alignées. */
function Rail({ filled, hidden }: { filled: boolean; hidden?: boolean }) {
  return (
    <span
      aria-hidden
      className={[
        "h-0.5 flex-1 rounded-full",
        hidden ? "bg-transparent" : filled ? "bg-or-500" : "bg-gray-100",
      ].join(" ")}
    />
  );
}

export function TripProgressSteps({ status }: { status: TripStatus | undefined }) {
  const activeIndex = stepIndexFor(status);

  // Rien à placer sur le rail : on n'affiche rien du tout.
  if (activeIndex === null) return null;

  return (
    <ol className="flex list-none items-start">
      {STEPS.map(({ key, label, Icon }, index) => {
        const isDone = index < activeIndex;
        const isCurrent = index === activeIndex;
        const isReached = isDone || isCurrent;

        return (
          <li key={key} className="flex flex-1 flex-col items-center">
            {/* Rails encadrant la pastille : chaque étape occupe une colonne de
                largeur égale, donc les pastilles se répartissent uniformément et
                le libellé reste centré dessous. */}
            <div className="flex w-full items-center">
              <Rail filled={isReached} hidden={index === 0} />

              <span
                className={[
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors",
                  isReached ? "bg-or-500" : "bg-gray-100",
                ].join(" ")}
              >
                <Icon
                  size={15}
                  color={isReached ? colors.navy800 : colors.gray300}
                  strokeWidth={2.5}
                  aria-hidden
                />
              </span>

              <Rail filled={isDone} hidden={index === STEPS.length - 1} />
            </div>

            <AppText
              as="span"
              variant={isCurrent ? "tinyBold" : "tiny"}
              color={isReached ? colors.navy800 : colors.gray500}
              className="mt-1.5 whitespace-nowrap"
            >
              {label}
            </AppText>
          </li>
        );
      })}
    </ol>
  );
}
